/**
 * SMS service — operator send, templates, history and the review queue.
 *
 * ⚠️ EVERY read here goes through a SECURITY DEFINER RPC, not a table select. sms_tracking,
 * sms_templates, sms_inbound_messages and sms_send_attempts are definer-only: RLS is enabled with
 * no policies and no grants to `authenticated`. Access is decided by role inside the function
 * (can_read_sms_activity / can_send_operator_sms), which is why a direct .from("sms_tracking")
 * silently returns nothing.
 *
 * ⚠️ SENDING IS PER-ENVIRONMENT, not a global switch. It used to be a bare `false` here, which
 * meant flipping it on for production would have armed QA at the same time — and QA shares
 * production's Sinch service plan AND short code, so a QA test would have texted a real person
 * from the real short code. It now reads the `operatorSmsSending` feature flag: true in production,
 * false in QA and development, where the composer stays fully usable and refuses at the last step.
 */
import { supabase } from "./supabase";
import { isFeatureEnabled } from "../utils/featureFlags";
import type { UUID } from "../types/common.types";

/** Whether a composed message is handed to the provider, per environment. See the file header. */
export const SENDING_ENABLED = isFeatureEnabled("operatorSmsSending");

export interface SmsSegmentPlan {
  encoding: "GSM7" | "UCS2";
  units: number;
  segments: number;
  per_segment: number;
  /** character offsets where the carrier will split the message */
  boundaries: number[];
}

export interface SmsPreflight {
  allowed: boolean;
  reason: string | null;
  consent_state:
    | "opted_in"
    | "opt_in_false"
    | "unknown"
    | "opted_out"
    | "no_usable_phone"
    | "number_suppressed";
  requires_disclosure: boolean;
  phone_e164: string | null;
  phone_masked: string | null;
  disclosure_version: string | null;
  disclosure_text: string | null;
  footer_version: string;
  footer_text: string;
  footer_units: number;
}

export interface SmsHistoryRow {
  id: UUID;
  phone_number: string;
  message_body: string;
  send_kind: string;
  consent_basis: string | null;
  provider_status: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  failed_at: string | null;
  failure_reason: string | null;
  segments_used: number | null;
  sent_by_role: string | null;
  created_at: string;
  /** "mms" when the message carried an image (migration 2026-09-28-008). */
  transport?: "sms" | "mms";
  media?: SmsMediaRef[];
  /** Set when the provider MAY have queued it (timeout, malformed answer): not failed, not resent. */
  uncertain_dispatch?: { reason: string; at: string } | null;
}

export interface SmsMediaRef {
  media_id: UUID;
  content_type: string;
  storage_path: string;
  sha256: string;
}

export interface SmsTemplate {
  folder_name?: string | null;
  id: UUID;
  name: string;
  description: string | null;
  body: string;
  character_count: number | null;
  created_at: string;
}

export interface SmsReviewItem {
  kind: "inbound" | "attempt";
  ref_id: string;
  phone_masked: string;
  occurred_at: string;
  classification: string | null;
  detail: string;
  body: string | null;
  contact_id: UUID | null;
  contact_name: string | null;
}

/** Rendering only — never trusted by the send, which re-decides under a row lock. */
export async function preflight(contactId: UUID): Promise<SmsPreflight> {
  const { data, error } = await supabase.rpc("sms_operator_send_preflight", {
    p_contact_id: contactId,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as SmsPreflight;
}

/** Segment plan, computed server-side so the composer and the send agree on the count. */
export async function segmentPlan(body: string): Promise<SmsSegmentPlan> {
  // ⚠️ p_text, NOT p_body. PostgREST resolves an RPC by its ARGUMENT NAMES, so a wrong name is a
  // 404 at runtime, not a type error -- the segment counter silently did nothing while the page
  // looked perfectly healthy. Caught by the browser render check, not by tsc.
  const { data, error } = await supabase.rpc("sms_segment_plan", {
    p_text: body,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as SmsSegmentPlan;
}

export async function getContactSmsHistory(
  contactId: UUID,
  limit = 50,
): Promise<SmsHistoryRow[]> {
  const { data, error } = await supabase.rpc("sms_contact_history", {
    p_contact_id: contactId,
    p_limit: limit,
  });
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as SmsHistoryRow[];
}

export async function listTemplates(): Promise<SmsTemplate[]> {
  const { data, error } = await supabase.rpc("sms_list_templates");
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as SmsTemplate[];
}

export async function getReviewQueue(limit = 100): Promise<SmsReviewItem[]> {
  const { data, error } = await supabase.rpc("sms_review_queue", {
    p_limit: limit,
  });
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as SmsReviewItem[];
}

/**
 * Marking an inbound reviewed does NOT change suppression. An ambiguous message suppressed the
 * number deliberately; undoing that is a separate, audited release by an admin.
 */
export async function resolveInboundReview(inboundId: string, note: string) {
  const { data, error } = await supabase.rpc("sms_resolve_inbound_review", {
    p_inbound_id: Number(inboundId),
    p_note: note,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as { ok: boolean; note?: string };
}

/**
 * `was_sent` closes the message with no further attempts — the member may already have it.
 * `was_not_sent` allows a retry, subject to every normal guard.
 */
export async function resolveAttemptReview(
  attemptId: string,
  disposition: "was_sent" | "was_not_sent",
  note: string,
) {
  const { data, error } = await supabase.rpc("sms_resolve_attempt_review", {
    p_attempt_id: attemptId,
    p_disposition: disposition,
    p_note: note,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as { ok: boolean; reason?: string };
}

// --------------------------------------------------------------------------------------------- MMS
/** What the SERVER says about sending images here. The browser never decides this on its own. */
export interface MmsStatus {
  enabled: boolean;
  reason: string | null;
  maxBytes: number;
}

export async function getMmsStatus(): Promise<MmsStatus> {
  const { data, error } = await supabase.functions.invoke("sms-media", {
    body: { action: "status" },
  });
  if (error || !data) {
    return { enabled: false, reason: "unavailable", maxBytes: 1_000_000 };
  }
  return {
    enabled: data.mms_enabled === true,
    reason: data.reason ?? null,
    maxBytes: Number(data.max_bytes) || 1_000_000,
  };
}

export interface UploadedSmsMedia {
  media_id: UUID;
  sha256: string;
  content_type: string;
  byte_size: number;
  width: number | null;
  height: number | null;
  preview_url: string | null;
}

/** Reads the server's reason out of a non-2xx edge-function response. */
async function functionError(error: unknown): Promise<string> {
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const j = await ctx.clone().json();
      return j.message ?? j.detail ?? j.error ?? String(error);
    } catch {
      /* fall through */
    }
  }
  return error instanceof Error ? error.message : String(error);
}

const MEDIA_ERRORS: Record<string, string> = {
  MEDIA_TOO_LARGE: "The image is too large to send.",
  MEDIA_UNSUPPORTED: "Only JPEG and PNG images can be sent.",
  MEDIA_EMPTY: "The image is empty.",
  SMS_NOT_AUTHORIZED: "You do not have permission to send text messages.",
};

export async function uploadSmsMedia(
  contactId: UUID,
  image: Blob,
  contentType: string,
): Promise<UploadedSmsMedia> {
  const { data, error } = await supabase.functions.invoke(
    `sms-media?contact_id=${encodeURIComponent(contactId)}`,
    { body: image, headers: { "Content-Type": contentType } },
  );
  if (error) {
    const msg = await functionError(error);
    throw new Error(MEDIA_ERRORS[msg] ?? msg);
  }
  return data as UploadedSmsMedia;
}

/** Short-lived links for images in history. Read access to SMS activity governs what is returned. */
export async function getSmsMediaUrls(
  mediaIds: UUID[],
): Promise<Record<string, string>> {
  if (mediaIds.length === 0) {
    return {};
  }
  const { data, error } = await supabase.functions.invoke("sms-media", {
    body: { action: "view", media_ids: mediaIds },
  });
  if (error) {
    throw new Error(await functionError(error));
  }
  return (data?.urls ?? {}) as Record<string, string>;
}

/** The server's answer to a send, reduced to what the composer must say. */
export type SendOutcome =
  | { kind: "accepted"; transport: "sms" | "mms"; trackingId: string }
  | { kind: "already_sent"; trackingId: string }
  | { kind: "not_sent"; trackingId: string; reason: string }
  | { kind: "unknown"; trackingId: string; note: string };

export interface SendArgs {
  contactId: UUID;
  body: string;
  templateId?: UUID | null;
  disclosureAck: boolean;
  disclosureVersion: string | null;
  /** the destination the composer displayed; the RPC refuses a mismatch */
  expectedPhone: string;
  /** idempotency key, generated once per composed message */
  requestKey: string;
  /** MMS: the uploaded image (one). Absent => SMS. */
  mediaIds?: UUID[];
}

export async function sendOperatorSms(args: SendArgs): Promise<SendOutcome> {
  if (!SENDING_ENABLED) {
    // Deliberate local refusal. The whole path exists and is tested; what is missing is
    // ACTIVATION — a registered Sinch callback, a cron for the retry claim, and audit clearance.
    // Refusing here rather than hiding the button keeps the composer honest and reviewable.
    throw new Error(
      "SMS sending is not enabled yet. The message was not sent and nothing was recorded.",
    );
  }
  const { data, error } = await supabase.functions.invoke("send-contact-sms", {
    body: {
      contact_id: args.contactId,
      body: args.body,
      template_id: args.templateId ?? null,
      disclosure_ack: args.disclosureAck,
      disclosure_version: args.disclosureVersion,
      expected_phone: args.expectedPhone,
      request_key: args.requestKey,
      ...(args.mediaIds?.length ? { media_ids: args.mediaIds } : {}),
    },
  });
  if (error) {
    throw new Error(await functionError(error));
  }
  return toSendOutcome(data);
}

/**
 * Never report "sent" for something that was not: a replay that was not ours to dispatch, and an
 * outcome the provider did not confirm, each get their own answer.
 */
export function toSendOutcome(
  data: Record<string, unknown> | null,
): SendOutcome {
  const trackingId = String(data?.tracking_id ?? "");
  const status = data?.status as string | undefined;
  if (status === "outcome_unknown") {
    return {
      kind: "unknown",
      trackingId,
      note: String(data?.note ?? "The provider did not confirm this message."),
    };
  }
  if (status === "already_sent") {
    return { kind: "already_sent", trackingId };
  }
  if (status === "not_dispatched") {
    return {
      kind: "not_sent",
      trackingId,
      reason: String(data?.reason ?? "not dispatched"),
    };
  }
  return {
    kind: "accepted",
    transport: data?.transport === "mms" ? "mms" : "sms",
    trackingId,
  };
}

export async function saveTemplate(
  values: Pick<SmsTemplate, "name" | "body" | "description" | "folder_name">,
  original?: SmsTemplate,
): Promise<SmsTemplate> {
  const { data, error } = await supabase.rpc("sms_save_template", {
    p_id: original?.id ?? null,
    p_name: values.name,
    p_body: values.body,
    p_description: values.description,
    p_folder_name: values.folder_name ?? null,
    p_expected_folder_name: original?.folder_name ?? null,
    p_expected_body: original?.body ?? null,
    p_expected_name: original?.name ?? null,
    p_expected_description: original?.description ?? null,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as SmsTemplate;
}

export async function archiveTemplate(id: UUID): Promise<void> {
  const { error } = await supabase.rpc("sms_archive_template", { p_id: id });
  if (error) {
    throw new Error(error.message);
  }
}

export type SmsConsentStatus =
  | "opted_in"
  | "opted_out"
  | "number_suppressed"
  | "no_usable_phone"
  | "unknown";
export async function getSmsConsentStatus(
  contactId: UUID,
): Promise<SmsConsentStatus> {
  const { data, error } = await supabase.rpc("sms_contact_consent_status", {
    p_contact_id: contactId,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as SmsConsentStatus;
}

/**
 * The single SMS status (migration 2026-10-26-998), for the contact's PRIMARY phone (where texts go). Display only:
 * sending still requires a recorded opt-in and checks the number ledger exactly as before; nothing sends on this.
 *   opted_out          the member EXPLICITLY opted out (STOP reply, member-app opt-out, staff recording the member's
 *                      request, recorded historical Salesforce opt-out)
 *   no_consent_on_file the CRM holds no data confirming an opt-in (never means the member declined; includes
 *                      contacts whose opt-out flag was imported in bulk with no evidence of a request)
 */
export type SmsSingleStatus =
  | "can_text"
  | "opted_out"
  | "number_blocked"
  | "no_mobile"
  | "no_consent_on_file";
export type SmsBlockReason =
  | "provider_stop"
  | "undeliverable"
  | "complaint"
  | "manual"
  | "opt_out_flag";
export interface SmsSingleStatusResult {
  sms_status: SmsSingleStatus;
  sms_block_reason: SmsBlockReason | null;
}
const SMS_SINGLE_STATUSES: readonly string[] = [
  "can_text",
  "opted_out",
  "number_blocked",
  "no_mobile",
  "no_consent_on_file",
];
const SMS_BLOCK_REASONS: readonly string[] = [
  "provider_stop",
  "undeliverable",
  "complaint",
  "manual",
  "opt_out_flag",
];
export async function getSmsSingleStatus(
  contactId: UUID,
): Promise<SmsSingleStatusResult> {
  const { data, error } = await supabase.rpc("sms_contact_single_status", {
    p_contact_id: contactId,
  });
  if (error) {
    throw new Error(error.message);
  }
  const d = (data ?? {}) as Record<string, unknown>;
  if (
    typeof d.sms_status !== "string" ||
    !SMS_SINGLE_STATUSES.includes(d.sms_status)
  ) {
    throw new Error("Unexpected SMS status");
  }
  return {
    sms_status: d.sms_status as SmsSingleStatus,
    sms_block_reason:
      typeof d.sms_block_reason === "string" &&
      SMS_BLOCK_REASONS.includes(d.sms_block_reason)
        ? (d.sms_block_reason as SmsBlockReason)
        : null,
  };
}

export interface OperatorConsentInput {
  contactId: string;
  expectedPhone: string;
  optedIn: boolean;
  note: string;
  conversationConfirmed: boolean;
  resubscribe: boolean;
  requestId: string;
  /** sms_consent_revision() read when the form opened; the save is refused if it changed (audit R2-T1). */
  expectedRevision?: string | null;
  /** When true the server refuses a NULL/blank expectedRevision (the CRM form always sets it). */
  requireRevision?: boolean;
}
/** Fingerprint of this number's consent state, read when the consent form opens (migration 2026-09-30-011). */
export async function getSmsConsentRevision(
  contactId: string,
): Promise<string | null> {
  const { data, error } = await supabase.rpc("sms_consent_revision", {
    p_contact_id: contactId,
  });
  if (error) {
    throw new Error(error.message);
  }
  return (data as string | null) ?? null;
}
export async function recordOperatorConsent(input: OperatorConsentInput) {
  const { data, error } = await supabase.rpc("sms_record_operator_consent", {
    p_contact_id: input.contactId,
    p_expected_phone: input.expectedPhone,
    p_opted_in: input.optedIn,
    p_note: input.note,
    p_conversation_confirmed: input.conversationConfirmed,
    p_resubscribe: input.resubscribe,
    p_request_id: input.requestId,
    p_expected_revision: input.expectedRevision ?? null,
    // Protected path (audit R3-T1): the server refuses a NULL/blank revision instead of skipping the guard.
    p_require_revision: input.requireRevision ?? false,
  });
  if (error) {
    throw new Error(error.message);
  }
  if (!data?.ok) {
    throw new Error(
      "The consent update could not be confirmed. Refresh and review the current status.",
    );
  }
  return data;
}
export interface OperatorConsentEvent {
  opted_in: boolean;
  actor_name: string;
  actor_role: string;
  note: string;
  recorded_at: string;
}
export async function getOperatorConsentHistory(
  contactId: string,
): Promise<OperatorConsentEvent[]> {
  const { data, error } = await supabase.rpc("sms_operator_consent_history", {
    p_contact_id: contactId,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}
